import { Skeleton, useToggle } from '@commenda/kakashi';
import type { IIndirectTaxFilingReviewGroup } from '@commenda/ui/indirect-tax/common/types';
import { ServiceRequestModel } from '@commenda/utils';
import { onClickActiveService } from 'components/HomeScreen/ActiveServices';
import { useRouter } from 'next/router';
import {
  ACTIVE_SERVICES_GRID_CLASSNAME,
  ActiveServicesRow,
} from './ActiveServicesRow';
import { FilingReviewRow } from './FilingReviewRow';
import { SectionTitle } from './SectionTitle';
import { useActiveServices, type ActiveService } from './useActiveServices';
import { useFilingReviewGroups } from './useFilingReviewGroups';

export const ActiveServices = () => {
  const router = useRouter();

  const { services, isLoading: isServicesLoading } = useActiveServices();
  const { filingReviewGroups, isLoading: isFilingReviewLoading } =
    useFilingReviewGroups();

  const isLoading = isServicesLoading || isFilingReviewLoading;

  const sectionVisibility = useToggle(true);

  const handleOpen = (service: ActiveService) => {
    onClickActiveService(service, router);
  };

  const handleOpenFilingReview = (
    filingReviewGroup: IIndirectTaxFilingReviewGroup
  ) => {
    router.push(
      ServiceRequestModel.getCompanyFilingsListLink(
        filingReviewGroup.corporationId
      )
    );
  };

  return (
    <section className="flex flex-col gap-lg">
      <SectionTitle
        title="Active services"
        isExpanded={sectionVisibility.visible}
        onToggle={sectionVisibility.toggle}
        className="pl-lg"
      />

      {sectionVisibility.visible && (
        <div className="flex flex-col">
          <div
            className={`${ACTIVE_SERVICES_GRID_CLASSNAME} border-b border-border-secondary py-md`}
          >
            <span className="txt-extra-small-medium text-text-secondary">
              Service name
            </span>
            <span className="txt-extra-small-medium text-text-secondary">
              Entity
            </span>
            <span className="txt-extra-small-medium text-text-secondary">
              Status
            </span>
            <span className="txt-extra-small-medium text-text-secondary">
              Current assignee
            </span>
          </div>

          {isLoading && (
            <div className="flex flex-col gap-md py-md">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          )}

          {!isLoading &&
            services.length === 0 &&
            filingReviewGroups.length === 0 && (
              <div className="py-3xl text-center txt-small-regular text-text-secondary">
                No active services
              </div>
            )}

          {!isLoading &&
            filingReviewGroups.map((filingReviewGroup) => (
              <FilingReviewRow
                key={filingReviewGroup.corporationId}
                filingReviewGroup={filingReviewGroup}
                onOpen={handleOpenFilingReview}
              />
            ))}

          {!isLoading &&
            services.map((service) => (
              <ActiveServicesRow
                key={service.id}
                service={service}
                onOpen={handleOpen}
              />
            ))}
        </div>
      )}
    </section>
  );
};
